import Icon from '@/components/ui/AppIcon';

interface Category {
  id: string;
  icon: string;
  title: string;
  description: string;
  topics: string[];
  color: string;
}

interface SupportCategoriesProps {
  onCategorySelect: (categoryId: string) => void;
}

export default function SupportCategories({ onCategorySelect }: SupportCategoriesProps) {
  const categories: Category[] = [
    {
      id: 'technical',
      icon: 'WrenchScrewdriverIcon',
      title: 'Technical Manufacturing',
      description: 'CNC machining, additive manufacturing, material specifications',
      topics: ['Process Selection', 'Material Guidance', 'Tolerance Requirements', 'Surface Finishing'],
      color: 'from-accent to-secondary'
    },
    {
      id: 'compliance',
      icon: 'ShieldCheckIcon',
      title: 'Compliance & Certification',
      description: 'AS9100, ITAR, quality standards, regulatory requirements',
      topics: ['AS9100 Certification', 'ITAR Compliance', 'Quality Documentation', 'Audit Support'],
      color: 'from-aerospace-green to-secondary'
    },
    {
      id: 'project',
      icon: 'ClipboardDocumentCheckIcon',
      title: 'Project Management',
      description: 'Timeline planning, capacity assessment, project tracking',
      topics: ['Project Planning', 'Timeline Optimization', 'Capacity Analysis', 'Progress Tracking'],
      color: 'from-mission-orange to-secondary'
    },
    {
      id: 'platform',
      icon: 'ComputerDesktopIcon',
      title: 'Platform & Portal',
      description: 'Account access, platform features, integration support',
      topics: ['Account Setup', 'Portal Navigation', 'API Integration', 'Data Export'],
      color: 'from-accent to-primary'
    },
    {
      id: 'quality',
      icon: 'BeakerIcon',
      title: 'Quality Assurance',
      description: 'Testing protocols, validation processes, quality reports',
      topics: ['Testing Methods', 'Validation Protocols', 'Quality Reports', 'Inspection Standards'],
      color: 'from-success to-secondary'
    },
    {
      id: 'partnership',
      icon: 'UserGroupIcon',
      title: 'Partnership & Onboarding',
      description: 'Vendor qualification, facility tours, capability assessments',
      topics: ['Vendor Qualification', 'Facility Tours', 'Capability Assessment', 'Partnership Terms'],
      color: 'from-warning to-secondary'
    }
  ];

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-accent/10 border border-accent/20 rounded-full mb-6">
            <Icon name="RectangleStackIcon" size={20} className="text-accent" />
            <span className="font-cta text-sm text-accent tracking-wider">SUPPORT CATEGORIES</span>
          </div>

          <h2 className="font-headline text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Comprehensive Support Across
            <span className="block text-accent mt-2">All Manufacturing Domains</span>
          </h2>

          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Our specialized support teams provide expert guidance across every aspect of aerospace manufacturing, from technical specifications to regulatory compliance.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((category) => (
            <div
              key={category.id}
              onClick={() => onCategorySelect(category.id)}
              className="group relative p-8 bg-card border border-border rounded-xl hover:border-accent transition-all duration-300 hover:shadow-glow cursor-pointer overflow-hidden"
            >
              {/* Gradient Background */}
              <div className={`absolute inset-0 bg-gradient-to-br ${category.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}></div>

              <div className="relative">
                {/* Icon */}
                <div className="w-16 h-16 bg-accent/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-accent/20 transition-colors duration-300">
                  <Icon name={category.icon as any} size={32} className="text-accent" />
                </div>

                {/* Content */}
                <h3 className="font-headline text-xl font-bold text-foreground mb-3 group-hover:text-accent transition-colors duration-300">
                  {category.title}
                </h3>

                <p className="text-sm text-technical-gray mb-6 leading-relaxed">
                  {category.description}
                </p>

                {/* Topics */}
                <div className="space-y-2">
                  {category.topics.map((topic, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <Icon name="CheckCircleIcon" size={16} className="text-accent flex-shrink-0" />
                      <span className="text-xs text-muted-foreground">{topic}</span>
                    </div>
                  ))}
                </div>

                {/* Arrow */}
                <div className="mt-6 flex items-center text-accent text-sm font-cta group-hover:translate-x-2 transition-transform duration-300">
                  <span>GET SUPPORT</span>
                  <Icon name="ArrowRightIcon" size={16} className="ml-2" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}